/**
 * Los rubros del catálogo como direcciones del sitio.
 *
 * Cada rubro tiene su propia página —/rubros/frenos-de-tambor— en vez de ser un filtro del
 * catálogo. La diferencia no es estética: un filtro es la misma página con otra lista adentro, y
 * un buscador la trata como una sola; una página propia tiene su título, su encabezado y su
 * texto, y puede aparecer cuando alguien busca "arandelas para moto".
 *
 * El nombre viene de la planilla del proveedor escrito como salga: "PUÑO ACELERADORES.",
 * "1/2 JGO JUNTAS DE MOTOR", "RETENES DE MOTOR  / ORING". La dirección se arma de ahí.
 */

/** El cajón de sastre de la planilla. No es un rubro: no tiene página ni entra en el índice. */
export const SIN_RUBRO = 'SIN CATEGORIA';

const ACENTOS = 'áàâäãéèêëíìîïóòôöõúùûüñçÁÀÂÄÃÉÈÊËÍÌÎÏÓÒÔÖÕÚÙÛÜÑÇ';
const SIN_ACENTOS = 'aaaaaeeeeiiiiooooouuuuncaaaaaeeeeiiiiooooouuuunc';

/** Sin acentos y en minúscula, que es como se compara un nombre con una dirección. */
function plano(texto: string): string {
  return [...texto.toLowerCase()]
    .map(letra => {
      const donde = ACENTOS.indexOf(letra);
      return donde < 0 ? letra : SIN_ACENTOS[donde];
    })
    .join('');
}

/**
 * La dirección de un rubro: "PUÑO ACELERADORES." es `puno-aceleradores`.
 *
 * Todo lo que no es una letra o un número se vuelve un guion, y los guiones seguidos se juntan
 * en uno. La barra de "1/2 JGO" también: queda `1-2-jgo-juntas-de-motor`, que se lee.
 */
export function slugDeRubro(nombre: string): string {
  return plano(nombre)
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** Si un nombre de rubro corresponde a una dirección. */
export function esElRubro(nombre: string, slug: string): boolean {
  return slugDeRubro(nombre) === slug.toLowerCase();
}

/**
 * Si el producto tiene un rubro de verdad.
 *
 * 130 productos caen en "SIN CATEGORIA" porque la planilla los dejó sin título de rubro. Eso no
 * es un rubro: mostrarlo en el título de la ficha o en las migas de pan es publicar el nombre de
 * un cajón interno.
 */
export function tieneRubro(rubro: string | null | undefined): rubro is string {
  return Boolean(rubro) && rubro!.trim().toUpperCase() !== SIN_RUBRO;
}

/** La dirección de la página del rubro, o null si el producto no tiene rubro. */
export function direccionDelRubro(rubro: string | null | undefined): string | null {
  return tieneRubro(rubro) ? `/rubros/${slugDeRubro(rubro)}` : null;
}
